import React, { FunctionComponent, useEffect, useMemo, useCallback, useRef, useState } from 'react';

import { map } from 'lodash';
import { useTranslation } from 'react-i18next';

import { SourceId } from 'admin/sources/Sources';
import { TenantId } from 'admin/tenants/Tenants';
import { SystemLabel } from 'common/comments2/Comments2.types';
import { useMessage, MessageActionType } from 'common/message-bar/MessageBar';
import { ResponseEditor } from 'common/response-editor/ResponseEditor';
import { useReplies } from 'reviews2/feed/review-yeti/Replies.store';
import { NoResponseRequired } from 'reviews2/feed/review-yeti/reply/editor/custom-elements/NoResponseRequired';
import { PartnerCredentials } from 'reviews2/feed/review-yeti/reply/editor/custom-elements/PartnerCredentials';
import { Lock } from 'reviews2/feed/review-yeti/reply/editor/ReplyEditor.types';
import { createEditorProps } from 'reviews2/feed/review-yeti/reply/editor/ReplyEditor.util';
import { useReview } from 'reviews2/feed/review-yeti/Review.store';
import { ContextProps, ReplyMode } from 'reviews2/feed/review-yeti/Review.types';
import { lockReview, unlockReview } from 'reviews2/Reviews.api';

export const ReplyEditor: FunctionComponent<ContextProps> = ({ context }) => {
    const { t } = useTranslation('reviews2');
    const [, dispatchMessage] = useMessage();
    const { profile } = context;
    const [{ review }, dispatchReview] = useReview();
    const { showEditor, repliesOptions } = context.options!;
    const [{ currentReply, replyTo, replyMode, editReply, canPublish }, dispatch] = useReplies();
    const editorRef = useRef<any>(null);
    const editorLockRef = useRef<Lock | null>(null);
    const canAdd = repliesOptions ? repliesOptions.canAdd : true;
    const hideEditor = replyMode === ReplyMode.Add && !canAdd;
    const timer = useRef<NodeJS.Timeout | null>(null);
    const [canEdit, setCanEdit] = useState(true);
    const isBozzutoApartmentRatings =
        review.review_source_id === SourceId.ApartmentRatings &&
        [TenantId.QATestInbox, TenantId.Bozzuto].includes(profile?.tenant?.id as number);

    const setRef = useCallback(
        (e: any) => {
            if (!editorRef.current || !hideEditor) {
                editorRef.current = e;
            }
        },
        [hideEditor],
    );

    const focusHandler = useCallback(
        async (...args) => {
            const [, type] = args;

            if (type === 'silent') {
                return;
            }

            try {
                const lock = await lockReview(review?.r4e_tenant_id, review.document_id);
                const lockDurationMs = (lock.expireTime - lock.lockedTime) * 1000;

                if (!lock.locked) {
                    editorRef.current.clearFocus();

                    setCanEdit(false);

                    dispatchMessage({
                        type: MessageActionType.ShowWarning,
                        text: t('CONVERSATION_LOCKED', { user: lock.lockedBy }),
                    });

                    setTimeout(() => {
                        setCanEdit(true);
                        editorRef.current.clearFocus();
                    }, Math.abs(lockDurationMs));
                } else {
                    editorLockRef.current = lock; // eslint-disable-line no-param-reassign

                    setCanEdit(true);
                    timer.current = setTimeout(() => {
                        editorRef.current?.clearFocus();
                    }, Math.abs(lockDurationMs));
                }
            } catch (e) {
                editorRef.current.clearFocus();
                setCanEdit(true);
            }
        },
        [review, dispatchMessage, t],
    );
    const blurHandler = useCallback(
        async (...args) => {
            const [, type] = args;

            if (type === 'silent') {
                return;
            }

            if (timer?.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }

            if (editorLockRef.current?.lockedByID === context.profile?.user?.id) {
                try {
                    await unlockReview(review?.r4e_tenant_id, review?.document_id);
                    editorLockRef.current = null; // eslint-disable-line no-param-reassign
                } catch (e) {
                    editorRef.current.clearFocus();
                }
            }
        },
        [context, review],
    );

    const categoryIDs = map(map(context.review.taxonomy_categories, 'category'), String);

    const editorProps = useMemo(() => {
        return createEditorProps({
            context,
            replyTo,
            currentReply,
            editReply,
            replyMode,
            dispatch,
            editorRef,
            editorLockRef,
            dispatchMessage,
            categoryIDs,
            canPublish,
            translate: t,
            canEdit,
            focusHandler,
            blurHandler,
            dispatchReview,
        });
    }, [
        context,
        currentReply,
        replyTo,
        replyMode,
        editReply,
        dispatch,
        dispatchMessage,
        categoryIDs,
        canPublish,
        t,
        canEdit,
        focusHandler,
        blurHandler,
        dispatchReview,
    ]);

    useEffect(() => {
        if (
            [ReplyMode.Edit, ReplyMode.Recommend].includes(replyMode!) &&
            !context.options?.editorOptions?.defaultText
        ) {
            const interval = setInterval(() => {
                if (editorRef.current) {
                    editorRef.current.setContents(editReply?.sanitized_text);

                    if (editReply?.expanded_post_media?.length) {
                        const editorMedia = editReply.expanded_post_media.map((media: any) => {
                            const isImage = ['photo'].includes(media.media_type);
                            const type = isImage ? 'image' : 'video';

                            return {
                                ...media,
                                isImage,
                                isVideo: !isImage,
                                mediaFormat: type,
                                type,
                            };
                        });

                        editorRef.current.setMedia(editorMedia);
                    }

                    const text = editorRef.current.getText();

                    if (text?.length || editReply.expanded_post_media?.length) {
                        clearInterval(interval);
                    }
                }
            }, 250);
        }
    }, [editReply, replyMode, editorRef, context.options?.editorOptions?.defaultText]);

    if (
        !showEditor ||
        review?.r4e_label_ids?.includes(SystemLabel.NotSyndicated) ||
        hideEditor // ACT-3537: hide when mode is Add and can not be new reponse added
    ) {
        return null;
    }

    if (review?.r4e_label_ids?.includes(SystemLabel.NoResponseRequired)) {
        return <NoResponseRequired />;
    }

    if (isBozzutoApartmentRatings) {
        return <PartnerCredentials />;
    }

    return (
        <ResponseEditor
            ref={setRef}
            {...editorProps}
            reviewId={context.review.document_id}
            reviewCreatedAt={context.review.post_created_at}
            locationId={context.review.r4e_location_uid}
            userId={context.profile?.user.id}
        />
    );
};
